import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TransferModal from './facility/TransferModal';
import BatchPackagingModal from './facility/BatchPackagingModal';
import UndoMoveModal from './facility/UndoMoveModal';
import SplitBatchModal from './facility/SplitBatchModal';
import FacilitySpiritsPanel from './facility/FacilitySpiritsPanel';
import { BatchStage, DropResult, canOperateFacility, classifyDrop, shapeAt, vesselKind } from '../utils/facilityTransfer';
import { createPortal } from 'react-dom';
import { Stage, Layer, Rect, Circle, Text } from 'react-konva';
import { useNavigate } from 'react-router-dom';
import { Site, Location, Equipment, DesignObject, Batch } from '../types/interfaces';
import '../App.css';
import { apiFetch } from '../utils/api';
import { beerColor, EMPTY_VESSEL_FILL, EMPTY_VESSEL_TEXT } from '../utils/beerColor';
import { API_BASE_URL } from '../config';
import { normalizeDesignObjects } from '../utils/facilityDesignerUtils';
import {
  enrichDesignObjectsWithLocations,
  readShapeLocationId,
  resolveFacilityLocation,
  isTaproomLocation,
} from '../utils/facilityLocationResolve';
import {
  readStoredFacilitySiteId,
  resolveFacilitySiteId,
  siteKind,
  sortSitesForRail,
  writeStoredFacilitySiteId,
} from '../utils/facilityViewSites';
import { lockMenuSwipe, unlockMenuSwipe } from '../utils/edgeSwipeMenu';
import { computeFacilityStageLayout, STAGE_W } from '../utils/facilityStageLayout';

const FACILITY_VIEW_DRAG_LOCK = 'facility-view-drag';

interface LocationOnHandItem {
  itemName: string;
  type: string;
  quantity: number;
  unit: string;
}

interface LocationOnHand {
  locationId: number;
  locationName: string;
  siteId: string;
  siteName?: string;
  totalCount: number;
  items: LocationOnHandItem[];
  /** Taproom = sold, tax paid (kegs until scanned back empty). Never brewery On Hand. */
  taproom?: boolean;
  countsAsInventory?: number;
  /** Taproom only: keg tags keg tracking has at this location, by product + keg size. Not a count. */
  kegTags?: LocationKegTagGroup[];
  kegTagCount?: number;
  /** Distillery finished-bottle racks: bottles from the spirits bottle ledger (Spirits Bottles). */
  spiritsBottles?: LocationSpiritsBottles;
  hiddenSpiritsPackaged?: number;
}

interface LocationSpiritsBottles {
  siteId: string;
  totalBottles: number;
  products: {
    productCode: string;
    productName: string;
    bottleSizeMl: number;
    bottlesPerCase: number;
    bottles: number;
    label: string;
  }[];
}

interface LocationKegTagGroup {
  productName: string;
  packageType: string;
  count: number;
  tags: { code: string; status: string | null; lastScanned: string | null }[];
}

/** Taproom storage shapes: amber, so they never read as brewery On Hand. */
export const TAPROOM_FILL = '#FFE0B2';

/** Unique in-progress batches on a design object (first = oldest). */
function vesselBatches(obj: DesignObject): Batch[] {
  if (!obj.equipmentId || !obj.batches || obj.batches.length === 0) return [];
  return Array.from(new Map(obj.batches.map((b) => [b.batchId, b])).values());
}

/** "30", "14.67" → for "30 bbl". */
export function fmtBbl(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return String(Math.round(Number(n) * 100) / 100);
}

/**
 * Size line(s) for a batch in a vessel. Brite tanks (or any partial vessel)
 * also show what's left: 30 bbl batch, 8 bbl left.
 */
export function vesselVolumeText(b: Batch): { size: string | null; left: string | null } {
  const size = fmtBbl(b.batchSize ?? b.volume ?? null);
  const remaining = b.vesselRemaining;
  const isBrite = /bright|brite/i.test(String(b.equipmentType || ''));
  const partial =
    remaining != null && b.batchSize != null && Math.abs(Number(remaining) - Number(b.batchSize)) > 0.005;
  const left = remaining != null && (isBrite || partial) ? fmtBbl(remaining) : null;
  return { size: size ? `${size} bbl` : null, left: left != null ? `${left} bbl left` : null };
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text;
}


interface FacilityViewProps {
  /** Optional default when nothing is stored yet. */
  siteId?: string;
}

const FacilityView: React.FC<FacilityViewProps> = ({ siteId: initialSiteId }) => {
  const navigate = useNavigate();
  const [siteId, setSiteId] = useState<string>(() => readStoredFacilitySiteId() || initialSiteId || '');
  const [sites, setSites] = useState<Site[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [objects, setObjects] = useState<DesignObject[]>([]);
  const [locationOnHand, setLocationOnHand] = useState<LocationOnHand | null>(null);
  const [locationInventoryLoading, setLocationInventoryLoading] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(null);
  const [selectedObject, setSelectedObject] = useState<DesignObject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mobileSitesOpen, setMobileSitesOpen] = useState(false);
  // Scale the fixed 800×600 layout down to fit phone widths.
  const canvasRef = React.useRef<HTMLDivElement>(null);

  // ---- Drag a batch to another vessel (transfer) or a storage location (package) ----
  const canOperate = useMemo(() => {
    try {
      return canOperateFacility(JSON.parse(localStorage.getItem('user') || '{}')?.role);
    } catch {
      return false;
    }
  }, []);
  const [reloadKey, setReloadKey] = useState(0);
  // Bumped by "Try again" so a failed first load can recover without a page refresh.
  const [sitesRetryKey, setSitesRetryKey] = useState(0);
  const [drag, setDrag] = useState<{ sourceId: string; batch: Batch; x: number; y: number; overId: string | null } | null>(null);
  const dragRef = React.useRef(drag);
  dragRef.current = drag;
  // Silence nav edge-swipe for the whole vessel-carry gesture (Android).
  useEffect(() => {
    if (!drag) return;
    lockMenuSwipe(FACILITY_VIEW_DRAG_LOCK);
    return () => unlockMenuSwipe(FACILITY_VIEW_DRAG_LOCK);
  }, [drag]);
  // Always release on unmount so a mid-press navigate cannot leave the global
  // lock (and body.tilly-menu-swipe-locked) stuck app-wide.
  useEffect(() => () => unlockMenuSwipe(FACILITY_VIEW_DRAG_LOCK), []);
  const pressRef = React.useRef<{
    obj: DesignObject;
    batch: Batch;
    touch: boolean;
    sx: number;
    sy: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const suppressClickUntilRef = React.useRef(0);
  const [transfer, setTransfer] = useState<{ batch: Batch; from: DesignObject; to: DesignObject; stage: BatchStage; regress?: boolean } | null>(null);
  const [undoBatchId, setUndoBatchId] = useState<string | null>(null);
  const [splitBatchId, setSplitBatchId] = useState<string | null>(null);
  const [packaging, setPackaging] = useState<{ batch: Batch; from: DesignObject; locationId: number } | null>(null);
  const [flash, setFlash] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);
  const showFlash = (text: string, tone: 'ok' | 'warn') => {
    setFlash({ text, tone });
    setTimeout(() => setFlash((f) => (f && f.text === text ? null : f)), 4000);
  };
  const [canvasWidth, setCanvasWidth] = useState(STAGE_W + 24);
  useEffect(() => {
    const measure = () => {
      const el = canvasRef.current;
      if (el && el.clientWidth) setCanvasWidth(el.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    // The canvas also changes width without a window resize (tablet push menu
    // opening/closing, site rail appearing), so watch the element itself.
    const RO = (window as any).ResizeObserver;
    const ro = RO && canvasRef.current ? new RO(measure) : null;
    if (ro) ro.observe(canvasRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  const closeLocationDrawer = useCallback(() => {
    setSelectedLocationId(null);
    setLocationOnHand(null);
    setLocationSearch('');
    if (!selectedEquipmentId) setSelectedObject(null);
  }, [selectedEquipmentId]);

  // Touch: the browser's follow-up click from the tap that opened the drawer can land
  // on the (new) backdrop and close it at once — the drawer "flashes". Ignore
  // backdrop clicks for a moment after opening.
  const drawerOpenedAtRef = React.useRef(0);
  useEffect(() => {
    if (selectedLocationId != null) drawerOpenedAtRef.current = Date.now();
  }, [selectedLocationId]);
  const onBackdropClick = () => {
    if (Date.now() - drawerOpenedAtRef.current < 600) return;
    closeLocationDrawer();
  };

  const selectSite = useCallback(
    (nextId: string) => {
      if (!nextId) return;
      setMobileSitesOpen(false);
      if (nextId === siteId) return;
      setSiteId(nextId);
      writeStoredFacilitySiteId(nextId);
      setSelectedLocationId(null);
      setSelectedEquipmentId(null);
      setSelectedObject(null);
      setLocationOnHand(null);
      setLocationSearch('');
    },
    [siteId]
  );

  // Load site list once; restore last-selected site when available.
  useEffect(() => {
    let cancelled = false;
    const loadSites = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const res = await apiFetch(`${API_BASE_URL}/api/sites`, { headers });
        if (!res.ok) throw new Error(`Sites fetch failed: HTTP ${res.status}`);
        const sitesData = await res.json();
        if (cancelled) return;
        const list: Site[] = Array.isArray(sitesData) ? sitesData : [];
        setSites(list);
        const resolved = resolveFacilitySiteId(list, initialSiteId, readStoredFacilitySiteId());
        if (resolved) {
          setSiteId(resolved);
          writeStoredFacilitySiteId(resolved);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError('Failed to load sites: ' + message);
        setSites([]);
      }
    };
    loadSites();
    return () => {
      cancelled = true;
    };
  }, [initialSiteId, sitesRetryKey]);

  // Load design / locations / equipment for the active site.
  useEffect(() => {
    if (!siteId) {
      setLocations([]);
      setEquipment([]);
      setObjects([]);
      return;
    }
    let cancelled = false;
    const loadSiteData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const [locationsData, equipmentData, designData] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/locations?siteId=${encodeURIComponent(siteId)}&includeTaproom=1`, {
            headers,
          }).then((res) => {
            if (!res.ok) throw new Error(`Locations fetch failed: HTTP ${res.status}`);
            return res.json();
          }),
          apiFetch(`${API_BASE_URL}/api/equipment?siteId=${encodeURIComponent(siteId)}`, {
            headers,
          }).then((res) => {
            if (!res.ok) throw new Error(`Equipment fetch failed: HTTP ${res.status}`);
            return res.json();
          }),
          apiFetch(`${API_BASE_URL}/api/facility-design?siteId=${encodeURIComponent(siteId)}`, {
            headers,
          }).then((res) => {
            if (!res.ok) throw new Error(`Facility designs fetch failed: HTTP ${res.status}`);
            return res.json();
          }),
        ]);
        if (cancelled) return;
        const locs: Location[] = Array.isArray(locationsData) ? locationsData : [];
        setLocations(locs);
        setEquipment(Array.isArray(equipmentData) ? equipmentData : []);
        const rawObjects =
          designData?.objects && Array.isArray(designData.objects)
            ? normalizeDesignObjects(designData.objects)
            : [];
        // Prod layouts often label Storage rooms without locationId — resolve by name/abbr + site.
        setObjects(enrichDesignObjectsWithLocations(rawObjects, locs, siteId));
        setError(
          designData?.error ||
            (designData?.objects && !Array.isArray(designData.objects) ? 'Invalid design data' : null)
        );
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unknown error';
        console.error('Fetch error:', err);
        setError('Failed to load facility design: ' + message);
        setLocations([]);
        setEquipment([]);
        setObjects([]);
      }
    };
    loadSiteData();
    return () => {
      cancelled = true;
    };
  }, [siteId, reloadKey]);

  // Location drawer: on-hand at the clicked storage location (any Facility View location).
  useEffect(() => {
    if (!selectedLocationId) {
      setLocationOnHand(null);
      setLocationInventoryLoading(false);
      return;
    }
    let cancelled = false;
    const fetchOnHand = async () => {
      setLocationInventoryLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await apiFetch(
          `${API_BASE_URL}/api/locations/${selectedLocationId}/on-hand?taproomKegsOnly=1`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `HTTP error! status: ${response.status}`);
        }
        const data: LocationOnHand = await response.json();
        if (!cancelled) setLocationOnHand(data);
      } catch (err) {
        console.error('Failed to load location inventory:', err);
        if (!cancelled) {
          setError(
            'Failed to load location inventory: ' +
              (err instanceof Error ? err.message : 'Unknown error')
          );
          setLocationOnHand(null);
        }
      } finally {
        if (!cancelled) setLocationInventoryLoading(false);
      }
    };
    void fetchOnHand();
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  // Dedupe Konva click/tap/pointerclick (touch synthesizes multiple families).
  const lastShapeClickRef = React.useRef<{ id: string; at: number }>({ id: '', at: 0 });
  const handleShapeClick = (obj: DesignObject) => {
    const now = Date.now();
    if (now < suppressClickUntilRef.current) return; // the release of a drag is not a tap
    if (lastShapeClickRef.current.id === obj.id && now - lastShapeClickRef.current.at < 400) {
      return;
    }
    lastShapeClickRef.current = { id: obj.id, at: now };

    const resolved = resolveFacilityLocation(obj, locations, siteId);
    const locationId =
      resolved?.locationId != null
        ? Number(resolved.locationId)
        : readShapeLocationId(obj);
    // Diagnostics for prod: confirm click fires + what resolve returned.
    // eslint-disable-next-line no-console
    console.info('[FacilityView] shape click', {
      siteId,
      shapeId: obj.id,
      type: obj.type,
      shape: obj.shape,
      name: obj.name,
      abbreviation: obj.abbreviation,
      rawLocationId: obj.locationId,
      resolvedLocationId: locationId,
      resolvedName: resolved?.name || null,
      locationCount: locations.length,
    });
    const inVessel = vesselBatches(obj);
    if (inVessel.length === 1) {
      navigate(`/production/${encodeURIComponent(inVessel[0].batchId)}`);
      return;
    }
    if (inVessel.length > 1) {
      setSelectedLocationId(null);
      setLocationOnHand(null);
      setSelectedEquipmentId(obj.equipmentId || null);
      setSelectedObject(obj);
      return;
    }
    if (locationId) {
      setSelectedLocationId(locationId);
      setSelectedEquipmentId(null);
      setSelectedObject(obj);
      setLocationSearch('');
      return;
    }
    setSelectedLocationId(null);
    setLocationOnHand(null);
    setLocationSearch('');
    setSelectedEquipmentId(obj.equipmentId || null);
    setSelectedObject(obj);
  };

  const filteredLocationItems = useMemo(() => {
    const items = locationOnHand?.items || [];
    const q = locationSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.itemName.toLowerCase().includes(q) || String(item.type || '').toLowerCase().includes(q)
    );
  }, [locationOnHand, locationSearch]);

  const filteredRackBottles = useMemo(() => {
    const products = locationOnHand?.spiritsBottles?.products || [];
    const q = locationSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) => p.productName.toLowerCase().includes(q) || p.productCode.toLowerCase().includes(q)
    );
  }, [locationOnHand, locationSearch]);

  const filteredKegTags = useMemo(() => {
    const groups = locationOnHand?.taproom ? locationOnHand.kegTags || [] : [];
    const q = locationSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) =>
        `${g.productName} ${g.packageType}`.toLowerCase().includes(q)
          ? g
          : { ...g, tags: g.tags.filter((t) => t.code.toLowerCase().includes(q)) }
      )
      .filter((g) => g.tags.length > 0)
      .map((g) => ({ ...g, count: g.tags.length }));
  }, [locationOnHand, locationSearch]);

  const selectedLocationName =
    locationOnHand?.locationName ||
    locations.find((loc) => loc.locationId === selectedLocationId)?.name ||
    'Storage location';

  // Desktop keeps the 800×600 board. Narrow screens crop to the drawn shapes
  // and scale that to fit, so tanks stay legible on a phone.
  const stageLayout = useMemo(() => computeFacilityStageLayout(objects, canvasWidth), [objects, canvasWidth]);
  const stageScale = stageLayout.scale;

  const dropCtx = useMemo(
    () => ({
      equipment,
      locationIdFor: (o: DesignObject) => {
        const resolved = resolveFacilityLocation(o, locations, siteId);
        return resolved?.locationId != null ? Number(resolved.locationId) : readShapeLocationId(o) || null;
      },
      isTaproomTarget: (o: DesignObject) => isTaproomLocation(resolveFacilityLocation(o, locations, siteId)),
    }),
    [equipment, locations, siteId]
  );

  const toDesign = (clientX: number, clientY: number) => {
    const el = (canvasRef.current?.querySelector('.konvajs-content') as HTMLElement | null) || canvasRef.current;
    const rect = el ? el.getBoundingClientRect() : { left: 0, top: 0 };
    return { x: (clientX - rect.left) / stageScale, y: (clientY - rect.top) / stageScale };
  };

  const dropResults = useMemo(() => {
    const out: Record<string, DropResult> = {};
    if (!drag) return out;
    const source = objects.find((o) => o.id === drag.sourceId);
    if (!source) return out;
    for (const o of objects) out[o.id] = classifyDrop(source, drag.batch, o, dropCtx);
    return out;
  }, [drag, objects, dropCtx]);

  const cancelPress = () => {
    if (pressRef.current?.timer) clearTimeout(pressRef.current.timer);
    pressRef.current = null;
    // Release only if carry has not started — drag effect owns the lock after pickUp.
    if (!dragRef.current) unlockMenuSwipe(FACILITY_VIEW_DRAG_LOCK);
  };

  const pickUp = (clientX: number, clientY: number) => {
    const press = pressRef.current;
    if (!press) return;
    if (press.timer) clearTimeout(press.timer);
    pressRef.current = null;
    const p = toDesign(clientX, clientY);
    // Lock before setDrag so edge-swipe tryCommit on the same touch cannot open the menu.
    lockMenuSwipe(FACILITY_VIEW_DRAG_LOCK);
    setDrag({ sourceId: press.obj.id, batch: press.batch, x: p.x, y: p.y, overId: null });
    try {
      (navigator as Navigator & { vibrate?: (ms: number) => boolean }).vibrate?.(25);
    } catch {
      /* no haptics */
    }
  };

  const finishDrag = (clientX: number, clientY: number) => {
    const d = dragRef.current;
    const press = pressRef.current;
    cancelPress();
    if (!d) return;
    setDrag(null);
    suppressClickUntilRef.current = Date.now() + 600;
    const source = objects.find((o) => o.id === d.sourceId);
    const p = toDesign(clientX, clientY);
    const target = shapeAt(objects, p.x, p.y);
    if (!source) return;
    const result = classifyDrop(source, d.batch, target, dropCtx);
    if (result.kind === 'invalid') showFlash(result.reason, 'warn');
    else if (result.kind === 'move' && target) setTransfer({ batch: d.batch, from: source, to: target, stage: result.stage });
    else if (result.kind === 'regress' && target) setTransfer({ batch: d.batch, from: source, to: target, stage: result.stage, regress: true });
    else if (result.kind === 'package') setPackaging({ batch: d.batch, from: source, locationId: result.locationId });
    void press;
  };

  /** Shape pointer down: mouse picks up after a small move; touch after a ~450ms hold (so scrolling still works). */
  const startPress = (obj: DesignObject, evt: any) => {
    if (!canOperate) return;
    const batch = vesselBatches(obj)[0];
    if (!batch) return;
    const touch = evt?.pointerType === 'touch' || (evt?.touches && evt.touches.length > 0) || evt?.type === 'touchstart';
    const pt = evt?.touches?.[0] || evt || {};
    // Android often delivers both pointerdown and touchstart. Do not cancelPress
    // between them — that briefly unlocks and can reset the long-press timer.
    const existing = pressRef.current;
    if (existing && existing.obj.id === obj.id && existing.batch.batchId === batch.batchId) {
      lockMenuSwipe(FACILITY_VIEW_DRAG_LOCK);
      if (touch && !existing.touch) {
        existing.touch = true;
        existing.sx = pt.clientX || existing.sx;
        existing.sy = pt.clientY || existing.sy;
        if (!existing.timer) {
          existing.timer = setTimeout(() => pickUp(pressRef.current?.sx || 0, pressRef.current?.sy || 0), 450);
        }
      }
      return;
    }
    cancelPress();
    pressRef.current = { obj, batch, touch, sx: pt.clientX || 0, sy: pt.clientY || 0, timer: null };
    // Vessel press: do not let a rightward finger drift open the nav drawer.
    // Runs before document useEdgeSwipeMenu touchstart (React/Konva → then bubble).
    lockMenuSwipe(FACILITY_VIEW_DRAG_LOCK);
    if (touch) {
      pressRef.current.timer = setTimeout(() => pickUp(pressRef.current?.sx || 0, pressRef.current?.sy || 0), 450);
    }
  };

  useEffect(() => {
    const move = (clientX: number, clientY: number, isTouch: boolean, e?: Event) => {
      const press = pressRef.current;
      const d = dragRef.current;
      if (!d) {
        if (!press) return;
        const dist = Math.hypot(clientX - press.sx, clientY - press.sy);
        if (press.touch) {
          if (dist > 10) cancelPress(); // finger moved before the hold: it's a scroll
          return;
        }
        if (dist > 6) pickUp(clientX, clientY);
        return;
      }
      if (isTouch && e && e.cancelable) e.preventDefault(); // no page scroll while carrying
      const p = toDesign(clientX, clientY);
      const over = shapeAt(objects, p.x, p.y);
      setDrag((cur) => (cur ? { ...cur, x: p.x, y: p.y, overId: over ? over.id : null } : cur));
    };
    const onPointerMove = (e: PointerEvent) => {
      if ((e as any).pointerType === 'touch') return; // handled by touch events
      move(e.clientX, e.clientY, false);
    };
    const onPointerUp = (e: PointerEvent) => {
      if ((e as any).pointerType === 'touch') return;
      if (dragRef.current) finishDrag(e.clientX, e.clientY);
      else cancelPress();
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) move(t.clientX, t.clientY, true, e);
    };
    const onTouchEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (dragRef.current && t) finishDrag(t.clientX, t.clientY);
      else cancelPress();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dragRef.current) {
        setDrag(null);
        cancelPress();
      }
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('keydown', onKey);
    };
  });

  const dropStroke = (o: DesignObject, base: string) => {
    if (!drag) return base;
    const r = dropResults[o.id];
    if (o.id === drag.sourceId) return '#1565C0';
    if (r?.kind === 'move' || r?.kind === 'package') return '#2E7D32';
    if (r?.kind === 'regress') return '#EF6C00';
    if (r?.kind === 'invalid' && drag.overId === o.id) return '#C62828';
    return base;
  };
  const dropStrokeWidth = (o: DesignObject, base: number) => {
    if (!drag) return base;
    const r = dropResults[o.id];
    if (o.id === drag.sourceId) return 3;
    if (r?.kind === 'move' || r?.kind === 'package' || r?.kind === 'regress') return drag.overId === o.id ? 6 : 3;
    if (r?.kind === 'invalid' && drag.overId === o.id) return 4;
    return base;
  };
  const dropDash = (o: DesignObject) =>
    drag && dropResults[o.id]?.kind === 'invalid' && drag.overId === o.id ? [6, 4] : undefined;

  const afterAction = (message: string) => {
    setTransfer(null);
    setPackaging(null);
    showFlash(message, 'ok');
    setReloadKey((k) => k + 1);
  };

  const orderedSites = sortSitesForRail(sites);
  const activeSite = orderedSites.find((s) => s.siteId === siteId) || null;
  const locationDrawerOpen = selectedLocationId != null;

  const renderSiteButton = (site: Site, variant: 'rail' | 'tab') => {
    const kind = siteKind(site);
    const selected = site.siteId === siteId;
    return (
      <button
        key={`${variant}-${site.siteId}`}
        type="button"
        className={`facility-site-item facility-site-item--${variant}${
          selected ? ' facility-site-item--selected' : ''
        }`}
        data-testid={`facility-site-${variant}-${site.siteId}`}
        aria-pressed={selected}
        onClick={() => selectSite(site.siteId)}
      >
        <span className="facility-site-item__name">{site.name}</span>
        <span className={`facility-site-badge facility-site-badge--${kind.toLowerCase()}`}>{kind}</span>
        <span className="facility-site-item__license" title="License / site ID">
          {site.siteId}
        </span>
      </button>
    );
  };

  return (
    <div className="page-container container facility-view" data-testid="facility-view-page">
      <header className="facility-view__header">
        <h2 className="app-header facility-view__title">Facility View</h2>
        {activeSite && (
          <p className="facility-view__subtitle" data-testid="facility-active-site-summary">
            <span className={`facility-site-badge facility-site-badge--${siteKind(activeSite).toLowerCase()}`}>
              {siteKind(activeSite)}
            </span>
            <span className="facility-view__subtitle-name">{activeSite.name}</span>
            <span className="facility-view__subtitle-id">{activeSite.siteId}</span>
          </p>
        )}
      </header>

      {error && (
        <div className="facility-view__error" role="alert">
          {error}{' '}
          <button
            type="button"
            className="facility-view__retry"
            onClick={() => {
              setError(null);
              setSitesRetryKey((k) => k + 1);
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </button>
        </div>
      )}

      {/* Mobile: horizontal site tabs under the title (clear of hamburger) */}
      <div className="facility-site-tabs" data-testid="facility-site-tabs" role="tablist" aria-label="Sites">
        {orderedSites.length === 0 ? (
          <span className="facility-site-tabs__empty">No sites available</span>
        ) : (
          orderedSites.map((site) => renderSiteButton(site, 'tab'))
        )}
      </div>

      {/* Compact mobile drawer for longer lists */}
      <div className="facility-site-mobile-bar">
        <button
          type="button"
          className="facility-site-mobile-bar__toggle"
          data-testid="facility-site-drawer-toggle"
          aria-expanded={mobileSitesOpen}
          onClick={() => setMobileSitesOpen((open) => !open)}
        >
          {mobileSitesOpen ? 'Hide sites' : 'All sites'}
          {activeSite ? ` · ${activeSite.name}` : ''}
        </button>
      </div>
      {mobileSitesOpen && (
        <div
          className="facility-site-drawer"
          data-testid="facility-site-drawer"
          role="dialog"
          aria-label="Choose site"
        >
          {orderedSites.map((site) => renderSiteButton(site, 'rail'))}
        </div>
      )}

      <div className="facility-view__body">
        <aside className="facility-site-rail" data-testid="facility-site-rail" aria-label="Sites">
          <h3 className="facility-site-rail__heading">Sites</h3>
          {orderedSites.length === 0 ? (
            <p className="facility-site-rail__empty">No sites available</p>
          ) : (
            <div className="facility-site-rail__list">
              {orderedSites.map((site) => renderSiteButton(site, 'rail'))}
            </div>
          )}

        </aside>

        <div className="facility-view__main">
        <div
          className={`facility-view__canvas${drag ? ' is-dragging' : ''}`}
          data-testid="facility-view-canvas"
          ref={canvasRef}
          onContextMenu={(e) => {
            if (pressRef.current || drag) e.preventDefault(); // Android long-press menu
          }}
        >
          <Stage
            width={Math.round(stageLayout.w * stageScale)}
            height={Math.round(stageLayout.h * stageScale)}
            scaleX={stageScale}
            scaleY={stageScale}
          >
            <Layer>
              {objects.length === 0 && (
                <Text
                  x={400}
                  y={300}
                  width={200}
                  text="No design objects found"
                  fontSize={16}
                  fontFamily="Arial"
                  fill="gray"
                  align="center"
                />
              )}
              {objects.map((obj: DesignObject) => {
                const inVessel = vesselBatches(obj);
                const occupied = inVessel.length > 0;
                const first = inVessel[0];
                const color = occupied ? beerColor(first) : null;
                const isVessel = !!obj.equipmentId;
                const isTaproomShape =
                  !isVessel && isTaproomLocation(resolveFacilityLocation(obj, locations, siteId));
                const fill = color
                  ? color.fill
                  : isVessel
                    ? EMPTY_VESSEL_FILL
                    : isTaproomShape
                      ? TAPROOM_FILL
                      : obj.shape === 'circle'
                        ? '#90CAF9'
                        : '#A5D6A7';
                const textColor = color ? color.text : isVessel ? EMPTY_VESSEL_TEXT : 'black';
                const extra = inVessel.length > 1 ? ` +${inVessel.length - 1}` : '';
                const shapeTestId = obj.locationId
                  ? `facility-shape-location-${obj.locationId}`
                  : obj.equipmentId
                    ? `facility-shape-equipment-${obj.equipmentId}`
                    : `facility-shape-${obj.id}`;
                // data-testid is consumed by the react-konva test mock (DOM button).
                const testHook = {
                  'data-testid': shapeTestId,
                  'data-fill': fill,
                } as Record<string, string>;
                const onPick = () => handleShapeClick(obj);
                if (obj.shape === 'circle') {
                  const r = obj.radius || 30;
                  const w = r * 1.8;
                  // Small vessels (e.g. barrels): abbreviation only; batch + product
                  // live in the chips under the canvas.
                  const small = r < 24;
                  const vol = occupied ? vesselVolumeText(first) : { size: null, left: null };
                  const lines =
                    occupied && !small
                      ? [
                          `${obj.abbreviation || ''}${extra}`,
                          first.batchId,
                          ...(vol.size ? [vol.size] : []),
                          ...(vol.left ? [vol.left.replace(' bbl left', ' left')] : []),
                        ]
                      : [`${obj.abbreviation || ''}${occupied ? extra : ''}`];
                  const nameW = Math.max(r * 2 + 12, 72);
                  return (
                    <React.Fragment key={obj.id}>
                      <Circle
                        x={obj.x}
                        y={obj.y}
                        radius={r}
                        fill={fill}
                        stroke={dropStroke(obj, occupied ? '#3a2a12' : '#6b7580')}
                        strokeWidth={dropStrokeWidth(obj, occupied ? 2 : 1)}
                        dash={dropDash(obj)}
                        preventDefault={false}
                        onPointerDown={(e: any) => startPress(obj, e?.evt)}
                        onTouchStart={(e: any) => startPress(obj, e?.evt)}
                        onClick={onPick}
                        onTap={onPick}
                        onPointerClick={onPick}
                        {...testHook}
                      />
                      <Text
                        x={obj.x - w / 2}
                        y={obj.y - r * 0.8}
                        width={w}
                        height={r * 1.6}
                        text={lines.join('\n')}
                        fontSize={
                          small
                            ? Math.max(7, Math.min(10, r / 1.9))
                            : occupied
                              ? Math.max(7, Math.min(12, (r * 1.6) / (lines.length * 1.2)))
                              : 12
                        }
                        fontStyle={occupied ? 'bold' : 'normal'}
                        lineHeight={1.2}
                        fontFamily="Arial"
                        fill={textColor}
                        align="center"
                        verticalAlign="middle"
                        wrap="none"
                        listening={false}
                      />
                      {occupied && !small && (
                        <Text
                          x={obj.x - nameW / 2}
                          y={obj.y + r + 3}
                          width={nameW}
                          height={11}
                          text={first.productName || 'Product'}
                          fontSize={9}
                          wrap="none"
                          fontFamily="Arial"
                          fill="#222"
                          align="center"
                          ellipsis
                          listening={false}
                        />
                      )}
                    </React.Fragment>
                  );
                }
                const w = obj.width || 100;
                const h = obj.height || 60;
                const rv = occupied ? vesselVolumeText(first) : { size: null, left: null };
                const lines = occupied
                  ? [
                      `${obj.abbreviation || ''}${extra}`,
                      first.batchId,
                      [rv.size, rv.left].filter(Boolean).join(' · '),
                      truncate(first.productName || '', Math.floor(w / 5.5)),
                    ].filter(Boolean)
                  : isTaproomShape
                    ? [obj.abbreviation || '', 'TAPROOM · sold']
                    : [obj.abbreviation || ''];
                return (
                  <React.Fragment key={obj.id}>
                    <Rect
                      x={obj.x}
                      y={obj.y}
                      width={w}
                      height={h}
                      fill={fill}
                      stroke={dropStroke(obj, occupied ? '#3a2a12' : 'black')}
                      strokeWidth={dropStrokeWidth(obj, occupied ? 2 : 1)}
                      dash={dropDash(obj)}
                      preventDefault={false}
                      onPointerDown={(e: any) => startPress(obj, e?.evt)}
                      onTouchStart={(e: any) => startPress(obj, e?.evt)}
                      cornerRadius={isVessel ? 6 : 0}
                      onClick={onPick}
                      onTap={onPick}
                      onPointerClick={onPick}
                      {...testHook}
                    />
                    <Text
                      x={obj.x + 2}
                      y={obj.y + 2}
                      width={w - 4}
                      height={h - 4}
                      text={lines.join('\n')}
                      fontSize={occupied ? 10 : 12}
                      fontStyle={occupied ? 'bold' : 'normal'}
                      lineHeight={1.15}
                      fontFamily="Arial"
                      fill={textColor}
                      align="center"
                      verticalAlign="middle"
                      listening={false}
                    />
                  </React.Fragment>
                );
              })}
              {drag && (
                <>
                  <Circle
                    x={drag.x}
                    y={drag.y}
                    radius={22}
                    fill={beerColor(drag.batch).fill}
                    opacity={0.85}
                    stroke="#1565C0"
                    strokeWidth={2}
                    listening={false}
                  />
                  <Text
                    x={drag.x - 40}
                    y={drag.y - 6}
                    width={80}
                    text={drag.batch.batchId}
                    fontSize={10}
                    fontStyle="bold"
                    fill={beerColor(drag.batch).text}
                    align="center"
                    listening={false}
                  />
                </>
              )}
            </Layer>
          </Stage>
        </div>
          {canOperate && (
            <p className="facility-drag-hint" data-testid="facility-drag-hint">
              {drag
                ? (() => {
                    const r = drag.overId ? dropResults[drag.overId] : null;
                    const over = objects.find((o) => o.id === drag.overId);
                    const name = over ? over.abbreviation || over.name : '';
                    if (r?.kind === 'move') return `Release to transfer ${drag.batch.batchId} to ${name}`;
                    if (r?.kind === 'regress') return `Release to move ${drag.batch.batchId} back to ${name} (needs a reason)`;
                    if (r?.kind === 'package') return `Release to package ${drag.batch.batchId} into ${name}`;
                    if (r?.kind === 'invalid') return r.reason;
                    return `Carrying ${drag.batch.batchId}: drop on a green vessel or storage area`;
                  })()
                : 'Tip: drag a batch (long-press on touch) to another tank to transfer it, or onto a storage area to package it.'}
            </p>
          )}
          {flash && (
            <div className={`facility-flash facility-flash--${flash.tone}`} role="status" data-testid="facility-flash">
              {flash.text}
            </div>
          )}
          {transfer && (
            <TransferModal
              batch={transfer.batch}
              from={transfer.from}
              to={transfer.to}
              defaultStage={transfer.stage}
              regress={!!transfer.regress}
              offerProductChange={vesselKind(transfer.to, equipment) === 'brite'}
              suggestedProductId={(transfer.to.batches || []).find((b) => b.batchId !== transfer.batch.batchId)?.productId ?? null}
              onCancel={() => setTransfer(null)}
              onDone={afterAction}
            />
          )}
          {splitBatchId && (
            <SplitBatchModal
              batchId={splitBatchId}
              onCancel={() => setSplitBatchId(null)}
              onDone={(m) => {
                setSplitBatchId(null);
                afterAction(m);
              }}
            />
          )}
          {undoBatchId && (
            <UndoMoveModal
              batchId={undoBatchId}
              equipment={equipment as any}
              onCancel={() => setUndoBatchId(null)}
              onDone={(m) => {
                setUndoBatchId(null);
                afterAction(m);
              }}
            />
          )}
          {packaging && (
            <BatchPackagingModal
              batch={packaging.batch}
              fromLabel={packaging.from.abbreviation || packaging.from.name || 'vessel'}
              locationId={packaging.locationId}
              locations={locations}
              onCancel={() => setPackaging(null)}
              onDone={afterAction}
            />
          )}
          {(() => {
            const occupied = objects.filter((o) => vesselBatches(o).length > 0);
            if (occupied.length === 0) return null;
            return (
              <ul className="facility-vessel-legend" data-testid="facility-vessel-legend" aria-label="Vessels in use">
                {occupied.map((o) => {
                  const list = vesselBatches(o);
                  const first = list[0];
                  const c = beerColor(first);
                  return (
                    <li key={o.id}>
                      <button
                        type="button"
                        className="facility-vessel-chip"
                        data-testid={`facility-vessel-chip-${o.abbreviation || o.id}`}
                        style={{ background: c.fill, color: c.text }}
                        onClick={() => handleShapeClick(o)}
                      >
                        <span className="facility-vessel-chip__abbr">{o.abbreviation}</span>
                        <span className="facility-vessel-chip__batch">{first.batchId}</span>
                        <span className="facility-vessel-chip__name">{first.productName || 'Product'}</span>
                        {(() => {
                          const v = vesselVolumeText(first);
                          return v.size || v.left ? (
                            <span className="facility-vessel-chip__vol" data-testid={`facility-vessel-vol-${o.abbreviation || o.id}`}>
                              {[v.size && v.left ? `${v.size} batch` : v.size, v.left].filter(Boolean).join(' · ')}
                            </span>
                          ) : null;
                        })()}
                        {list.length > 1 && (
                          <span className="facility-vessel-chip__count" data-testid={`facility-vessel-count-${o.abbreviation || o.id}`}>
                            +{list.length - 1}
                          </span>
                        )}
                      </button>
                      {canOperate && (
                        <button
                          type="button"
                          className="facility-vessel-chip__undo"
                          data-testid={`facility-undo-${first.batchId}`}
                          title={`Undo last move of ${first.batchId}`}
                          onClick={() => setUndoBatchId(first.batchId)}
                        >
                          ↶ Undo move
                        </button>
                      )}
                      {canOperate && (
                        <button
                          type="button"
                          className="facility-vessel-chip__undo"
                          data-testid={`facility-split-${first.batchId}`}
                          title={`Split part of ${first.batchId} into a new batch`}
                          onClick={() => setSplitBatchId(first.batchId)}
                        >
                          ⑂ Split
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            );
          })()}
          {siteId ? <FacilitySpiritsPanel siteId={siteId} reloadKey={reloadKey} /> : null}
          {selectedEquipmentId && selectedObject && !selectedLocationId && (
            <div className="facility-selection-panel" data-testid="facility-selection-panel">
              <h3>
                {equipment.find((eq) => eq.equipmentId === selectedEquipmentId)?.name ||
                  selectedObject.abbreviation ||
                  'Vessel'}
              </h3>
              {vesselBatches(selectedObject).length > 0 ? (
                <>
                  <h4>{vesselBatches(selectedObject).length} batches in this vessel</h4>
                  <ul data-testid="facility-equipment-batches">
                    {vesselBatches(selectedObject).map((batch: Batch) => {
                      const c = beerColor(batch);
                      return (
                        <li key={batch.batchId}>
                          <button
                            type="button"
                            className="facility-vessel-batch-link"
                            data-testid={`facility-vessel-batch-${batch.batchId}`}
                            onClick={() => navigate(`/production/${encodeURIComponent(batch.batchId)}`)}
                          >
                            <span className="facility-vessel-swatch" style={{ background: c.fill }} />
                            <strong>{batch.batchId}</strong> {batch.productName || 'Product'}
                            {(() => {
                              const v = vesselVolumeText(batch);
                              return [v.size, v.left].filter(Boolean).length
                                ? ` · ${[v.size, v.left].filter(Boolean).join(' · ')}`
                                : '';
                            })()}
                            {batch.stage ? ` (${batch.stage})` : ''}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <>
                  <h4>Empty</h4>
                  <p data-testid="facility-equipment-empty">No batches assigned</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/*
        Portal to body: .content and .page-container both use overflow-y:auto,
        which clips position:fixed descendants in prod — drawer never appeared.
      */}
      {locationDrawerOpen &&
        createPortal(
          <>
            <button
              type="button"
              className="facility-location-drawer__backdrop"
              data-testid="facility-location-drawer-backdrop"
              aria-label="Close location inventory"
              onClick={onBackdropClick}
            />
            <aside
              className="facility-location-drawer"
              data-testid="facility-location-drawer"
              role="dialog"
              aria-modal="true"
              aria-label={`Inventory at ${selectedLocationName}`}
            >
              <div className="facility-location-drawer__header">
                <div>
                  <h3 className="facility-location-drawer__title">{selectedLocationName}</h3>
                  <p
                    className="facility-location-drawer__meta"
                    data-testid="facility-location-drawer-count"
                  >
                    {locationInventoryLoading
                      ? 'Loading…'
                      : locationOnHand?.spiritsBottles
                        ? `${locationOnHand.spiritsBottles.totalBottles.toLocaleString()} bottle${
                            locationOnHand.spiritsBottles.totalBottles === 1 ? '' : 's'
                          } · ${filteredRackBottles.length} product${filteredRackBottles.length === 1 ? '' : 's'}`
                      : `${filteredLocationItems.length} item${
                          filteredLocationItems.length === 1 ? '' : 's'
                        }`}
                    {!locationInventoryLoading &&
                    !locationOnHand?.spiritsBottles &&
                    locationSearch.trim() &&
                    locationOnHand &&
                    filteredLocationItems.length !== locationOnHand.totalCount
                      ? ` of ${locationOnHand.totalCount}`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="facility-location-drawer__close"
                  data-testid="facility-location-drawer-close"
                  onClick={closeLocationDrawer}
                >
                  Close
                </button>
              </div>

              {(locationOnHand?.taproom ||
                isTaproomLocation(locations.find((l) => l.locationId === selectedLocationId))) && (
                <p className="facility-location-drawer__taproom" data-testid="facility-location-drawer-taproom" role="note">
                  <strong>Taproom: sold, tax paid.</strong> Kegs on tap or in the taproom cooler, until they are
                  scanned back empty. Not brewery On Hand and not in brewery totals. Cases count as sold at transfer and
                  don't show here.
                </p>
              )}

              <label className="facility-location-drawer__search-label">
                <span className="facility-location-drawer__search-caption">Search</span>
                <input
                  type="search"
                  className="facility-location-drawer__search"
                  data-testid="facility-location-drawer-search"
                  placeholder="Filter by name or type"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                />
              </label>

              <div
                className="facility-location-drawer__list"
                data-testid="facility-location-drawer-list"
              >
                {!locationInventoryLoading && locationOnHand?.spiritsBottles && (
                  <div data-testid="facility-location-drawer-bottles">
                    <h4 className="facility-location-drawer__section">Bottles on hand (Spirits Bottles)</h4>
                    {filteredRackBottles.length === 0 ? (
                      <p className="facility-location-drawer__empty" data-testid="facility-location-drawer-bottles-empty">
                        {locationSearch.trim() ? 'No matching products' : 'No bottles on hand'}
                      </p>
                    ) : (
                      <ul>
                        {filteredRackBottles.map((p) => (
                          <li key={`${p.productCode}-${p.bottleSizeMl}`}>
                            <div
                              className="facility-location-drawer__item"
                              data-testid={`facility-rack-bottles-${p.productCode}`}
                            >
                              <span className="facility-location-drawer__item-name">{p.productName}</span>
                              <span className="facility-location-drawer__item-type">{p.label}</span>
                              <span className="facility-location-drawer__item-qty">
                                {p.bottles.toLocaleString()} bottle{p.bottles === 1 ? '' : 's'}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      className="facility-location-drawer__tag"
                      data-testid="facility-location-drawer-spirits-bottles-link"
                      onClick={() =>
                        navigate(`/spirits/bottles?siteId=${encodeURIComponent(locationOnHand.spiritsBottles!.siteId)}`)
                      }
                    >
                      Open Spirits Bottles
                    </button>
                  </div>
                )}
                {locationInventoryLoading ? (
                  <p className="facility-location-drawer__empty">Loading inventory…</p>
                ) : locationOnHand?.spiritsBottles && filteredLocationItems.length === 0 ? null : filteredLocationItems.length === 0 ? (
                  <>
                    {locationOnHand?.taproom && (
                      <h4 className="facility-location-drawer__section">Taproom keg On Hand (sold, tax paid)</h4>
                    )}
                    <p className="facility-location-drawer__empty" data-testid="facility-location-drawer-empty">
                      {locationSearch.trim()
                        ? 'No matching items'
                        : locationOnHand?.taproom
                          ? 'No kegs counted in taproom On Hand'
                          : 'No inventory in this location'}
                    </p>
                  </>
                ) : (
                  <>
                  {locationOnHand?.taproom && (
                    <h4 className="facility-location-drawer__section">Taproom keg On Hand (sold, tax paid)</h4>
                  )}
                  <ul>
                    {filteredLocationItems.map((item) => (
                      <li key={`${item.type}-${item.itemName}`}>
                        <button
                          type="button"
                          className="facility-location-drawer__item"
                          data-testid={`facility-location-item-${item.itemName}`}
                          onClick={() =>
                            navigate(`/items/${encodeURIComponent(item.itemName)}`)
                          }
                        >
                          <span className="facility-location-drawer__item-name">
                            {item.itemName}
                          </span>
                          <span className="facility-location-drawer__item-type">{item.type}</span>
                          <span className="facility-location-drawer__item-qty">
                            {Number(item.quantity).toLocaleString(undefined, {
                              maximumFractionDigits: 4,
                            })}{' '}
                            {item.unit}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  </>
                )}
                {!locationInventoryLoading && locationOnHand?.taproom && (
                  <div className="facility-location-drawer__kegtags" data-testid="facility-location-drawer-kegtags">
                    <h4 className="facility-location-drawer__section">
                      Keg tags here in keg tracking ({locationOnHand.kegTagCount ?? 0})
                    </h4>
                    <p className="facility-location-drawer__hint">
                      Not a count: includes kegs never scanned back empty. A Taproom Keg Count confirms what is really here.
                    </p>
                    {filteredKegTags.length === 0 ? (
                      <p className="facility-location-drawer__empty">
                        {locationSearch.trim() ? 'No matching keg tags' : 'No keg tags at this location'}
                      </p>
                    ) : (
                      <ul>
                        {filteredKegTags.map((g) => (
                          <li key={`${g.productName}|${g.packageType}`}>
                            <details data-testid={`facility-kegtag-group-${g.productName} (${g.packageType})`}>
                              <summary className="facility-location-drawer__item">
                                <span className="facility-location-drawer__item-name">
                                  {g.productName} · {g.packageType}
                                </span>
                                <span className="facility-location-drawer__item-qty">
                                  {g.count} tag{g.count === 1 ? '' : 's'}
                                </span>
                              </summary>
                              <ul className="facility-location-drawer__tags">
                                {g.tags.map((t) => (
                                  <li key={t.code}>
                                    <button
                                      type="button"
                                      className="facility-location-drawer__tag"
                                      data-testid={`facility-kegtag-${t.code}`}
                                      onClick={() => navigate(`/keg-tracking?keg=${encodeURIComponent(t.code)}`)}
                                    >
                                      {t.code}
                                      {t.status ? ` · ${t.status}` : ''}
                                      {t.lastScanned ? ` · ${t.lastScanned}` : ''}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </aside>
          </>,
          document.body
        )}
    </div>
  );
};

export default FacilityView;
